import axios from 'axios';
import api from '../../utils/service/api';

// SR, SEG, PR, KO and RT* are derived/report modalities, not imaging modalities.
// When a report is created inside a CT/DX/CR study, Orthanc adds SR to the
// series list and ModalitiesInStudy. We must NOT surface these as the study modality.
const DERIVED_MODALITIES = ['SR', 'SEG', 'PR', 'KO', 'RTSTRUCT', 'RTDOSE', 'RTPLAN', 'RTRECORD', 'REG'];

export const getPrimaryModality = (seriesModality, modalitiesInStudy) => {
  // 1. If the series modality is a real imaging modality, use it as-is.
  if (seriesModality && !DERIVED_MODALITIES.includes(seriesModality.toUpperCase())) {
    return seriesModality;
  }
  // 2. Check ModalitiesInStudy (backslash-separated DICOM multi-value, e.g. "DX\SR")
  if (modalitiesInStudy) {
    const primary = String(modalitiesInStudy)
      .split('\\')
      .map(m => m.trim())
      .find(m => m && !DERIVED_MODALITIES.includes(m.toUpperCase()));
    if (primary) return primary;
  }
  // 3. Fall back to whatever is available (e.g. a pure SR-only study is legitimately SR).
  return seriesModality || modalitiesInStudy || 'N/A';
};

// Append a unique timestamp param to every Orthanc GET so the browser
// can never serve a stale cached response (Orthanc sends no Cache-Control).
const orthancGet = (url, config) =>
  axios.get(`${url}${url.includes('?') ? '&' : '?'}_t=${Date.now()}`, config);

export const getClinicalStudyUIDs = () => {
  return api({
    method: 'get',
    url: '/api/studies/clinical',
  });
};

// Returns the clinical-index health/enablement for the caller's tenant:
// { available, enabled, es_count, orthanc_count, in_sync }.  The All Studies
// page calls this once on mount to decide between server-side pagination
// (enabled) and the legacy client-side path.
export const getClinicalIndexHealth = async () => {
  try {
    const res = await api({ method: 'get', url: '/api/studies/clinical-index/health' });
    return res?.data?.data || {};
  } catch (e) {
    return {};
  }
};

// Fetch studies via the backend admin endpoint — server-side enrichment
// (SeriesData + statistics) replaces the previous browser-side multi-fetch.
// Project-owned studies (annotation projects) are filtered out client-side
// to preserve the same behaviour as the original implementation.
// Return shape is identical: { data: { data: [...studies], total: N } }
export const getStudiesForClinical = async (params = {}) => {
  try {
    const offset = params._offset || 0;
    const limit  = params._limit  || 50;

    // Single backend call. The /admin endpoint already excludes annotation-project
    // studies server-side.  When the tenant is on the ES index (server-side mode),
    // the caller passes filter/sort params below and the server returns just that
    // page; otherwise these are omitted and the server returns the full pool for
    // the legacy client-side path.
    const qs = [`offset=${offset}`, `limit=${limit}`];
    const passthrough = {
      search: params.search,
      patient_name: params.patient_name,
      patient_id: params.patient_id,
      accession: params.accession,
      modality: params.modality,
      study_date_from: params.study_date_from,
      study_date_to: params.study_date_to,
      report_status: params.report_status,
      status: params.status,
      sort: params.sort,
    };
    Object.entries(passthrough).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.push(`${k}=${encodeURIComponent(v)}`);
    });

    const response = await api({
      method: 'get',
      url: `/api/studies/admin?${qs.join('&')}`,
    });
    const payload = response?.data?.data;
    const allStudies = Array.isArray(payload?.data) ? payload.data : [];

    return {
      data: {
        data:  allStudies,
        total: typeof payload?.total === 'number' ? payload.total : 0,
        // True when the server held back rows beyond this page — i.e. the corpus
        // has outgrown the single-fetch client-side filter pool. Surfaced so the
        // table never silently reports a truncated row count as the study total.
        hasMore: payload?.has_more === true,
        // "es" when served from the clinical search index, else "orthanc".
        source: payload?.source || 'orthanc',
      },
    };
  } catch (error) {
    console.error('[StudiesService] getStudiesForClinical failed:', error);
    throw error;
  }
};

export const getStudyDetail = (studyId) => {
  return axios.get(`/orthanc/studies/${studyId}`);
};

// Bulk report status — ONE POST replaces N per-study /history round-trips.
// The backend fans out concurrently over the fast server↔Orthanc link.
// Response shape: { data: { <studyInstanceUID>: { status, timestamp } } }
// where status is already computed as 'submitted' | 'draft' | 'none'.
export const getBulkReportStatus = (studyInstanceUIDs = []) => {
  return api({
    method: 'post',
    url: '/api/reports/bulk-status',
    data: { study_instance_uids: studyInstanceUIDs },
  });
};

// Every study of the given studies' patients in this hospital, for the priors
// badge. Response shape: { data: { studies: [{ study_instance_uid, patient_id,
// study_date, report_status, ... }] } }; empty when the clinical index is off.
export const getPatientHistory = (studyInstanceUIDs = []) =>
  api({
    method: 'post',
    url: '/api/studies/patient-history',
    data: { study_instance_uids: studyInstanceUIDs },
  });

export const getReportForStudy = (studyInstanceUID) => {
  return api({
    method: 'get',
    url: `/api/reports/${encodeURIComponent(studyInstanceUID)}`,
  });
};

export const getAllReportsForStudy = (studyInstanceUID) => {
  return api({
    method: 'get',
    url: `/api/reports/${encodeURIComponent(studyInstanceUID)}/history`,
  });
};

export const uploadDICOMFiles = (data) => {
  return axios.post('/orthanc/instances', data);
};

export const getKeycloakUsers = (params = {}) => {
  return api({
    method: 'get',
    url: '/api/accounts/userinfo',
    params,
  });
};

export const getUserById = (userId) => {
  return api({
    method: 'get',
    url: `/api/accounts/userinfo/${encodeURIComponent(userId)}`,
  });
};

export const assignStudiesToUser = (data = {}) => {
  return api({
    method: 'post',
    url: '/api/studies/assign',
    data,
  });
};

export const unassignStudies = (studyInstanceUIDs = []) => {
  return api({
    method: 'post',
    url: '/api/studies/unassign',
    data: { study_instance_uids: studyInstanceUIDs },
  });
};

// POST body carries UIDs — no URL size limit, single round-trip regardless
// of how many studies the hospital has (replaces the old chunked GET approach).
export const getStudyAssignments = (studyInstanceUIDs = []) =>
  api({
    method: 'post',
    url: '/api/studies/assignments',
    data: { study_instance_uid: studyInstanceUIDs },
  }).then((res) => ({ data: { data: res.data?.data || {} } }));

export const deleteStudiesFromClinical = (data = {}) => {
  return api({
    method: 'post',
    url: '/api/studies/delete_clinical',
    data,
  });
};

export const getRadiologistWorklist = () => {
  return api({
    method: 'get',
    url: '/api/studies/worklist',
  });
};

